<script setup lang="ts">
import {computed, onMounted, ref} from 'vue';
import {ElOption, ElSelect} from 'element-plus';
import {createObject, deleteObject, listObjects, updateObject} from '@/base/callBackend';
import {showHideLoading} from '@/base/loadingState';
import {CabFteTarget, CabOrganization, uuidOf} from 'common';
// noinspection ES6UnusedImports - usages in <template> are not properly detected!
import {FTE_TARGET_CHANGE_EMIT, FTE_TARGET_REMOVE_EMIT} from '@/base/cabConstants';
import FteTargetStrip from '@/views/admin/FteTargetStrip.vue';
import PlusButton from '@/views/icons/PlusIcon.vue';
import GeneralFilter from '@/views/general/GeneralFilter.vue';

const alle          = ref<CabFteTarget[]>([]);
const organizations = ref<CabOrganization[]>([]);
const kwartaal      = ref<string>('');
const filterActive  = ref<boolean>(false);
const vanKwartaal   = computed<CabFteTarget[]>(() => alle.value.filter(i => kwartaal.value === '' || i.kwartaal === kwartaal.value));
const gefilterd     = ref<CabFteTarget[]>([]);
const kwartalen     = computed<string[]>(() => [...new Set(alle.value.map(i => i.kwartaal))].sort().reverse());

onMounted(async () => {
    showHideLoading(true);
    try {
        organizations.value = await listObjects(CabOrganization);
        alle.value          = await listObjects(CabFteTarget);
        kwartaal.value      = kwartalen.value[0] ?? '';
    } finally {
        showHideLoading(false);
    }
});

//=====================================================================================
async function onRemove(target: CabFteTarget) {
    await deleteObject(target);
    alle.value = alle.value.filter((i) => uuidOf(i) !== uuidOf(target));
}

async function onAdd() {
    filterActive.value        = false;
    const nieuw: CabFteTarget = new CabFteTarget();
    nieuw.kwartaal            = kwartaal.value;
    await createObject(nieuw);
    alle.value = [nieuw, ...alle.value];
}

async function onChange(target: CabFteTarget) {
    const uuid: string = uuidOf(target);
    await updateObject(target);
    alle.value = alle.value.map((i) => uuidOf(i) === uuid ? target : i);
}
</script>

<template>
    <div class="flex flex-col flex-1">
        <div class="flex flex-row flex-1 mb-1 items-center gap-2">
            <PlusButton @click="onAdd()"/>
            <div class="text-2xl flex-1 ml-2" data-testid="data-section-title">
                FTE targets
            </div>
            <el-select v-model="kwartaal" placeholder="kwartaal" class="w-32" clearable>
                <el-option v-for="q in kwartalen" :key="q" :value="q" :label="q"/>
            </el-select>
            <GeneralFilter v-model:active="filterActive"
                           :cab-list="vanKwartaal"
                           v-model:cab-list-filtered="gefilterd"
                           :id="'FteTargetList'"
            />
        </div>
        <div class="text-sm text-gray-500 mb-2">
            Een target per organisatie, kwartaal en metric, zoals in het Codebook. Nieuwe targets komen binnen via Upload.
        </div>
        <FteTargetStrip
            v-for="target in gefilterd"
            :key="uuidOf(target)"
            :target="target"
            :allOrganizations="organizations"
            v-on:[FTE_TARGET_REMOVE_EMIT]="onRemove"
            v-on:[FTE_TARGET_CHANGE_EMIT]="onChange"
        />
    </div>
</template>
